import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Info,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { QuestionType, CaseQuestionData } from "../types";

// Add custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

// Modal component
const Modal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[90%] max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b border-[#E6E8EB] flex justify-between items-center">
          <h3 className="text-lg font-semibold text-[#1F2024]">{title}</h3>
          <button
            onClick={onClose}
            className="text-[#474A52] hover:text-[#1F2024]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {children}
        </div>
      </div>
    </div>
  );
};

interface QuestionSectionProps {
  question: {
    text: string;
    number: number;
    availableInformation?: string[];
    graphs?: Array<{
      graph_url: string;
      graph_title: string;
    }>;
    questionType?: string;
  };
  activeTab: QuestionType;
  onTabChange: (tab: QuestionType) => void;
  allQuestions: Record<QuestionType, CaseQuestionData>;
  isLoading?: boolean;
}

const QuestionSection: React.FC<QuestionSectionProps> = ({
  question,
  activeTab,
  onTabChange,
  allQuestions,
  isLoading = false,
}) => {
  const [isInfoExpanded, setIsInfoExpanded] = useState(false);
  const [isGraphExpanded, setIsGraphExpanded] = useState(false);
  const [currentGraphIndex, setCurrentGraphIndex] = useState(0);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showGraphModal, setShowGraphModal] = useState(false);

  // Format question type to be more readable
  const formatQuestionType = (type: string): string => {
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Generate question tabs based on available questions in allQuestions
  const questionTabs = Object.entries(allQuestions)
    .map(([key, data]) => ({
      id: key as QuestionType,
      label: `Question ${data.case_question_number}`,
      number: data.case_question_number,
      questionType: data.questionType,
    }))
    .sort((a, b) => a.number - b.number);

  const handlePreviousGraph = () => {
    if (!question.graphs || question.graphs.length <= 1) return;
    setCurrentGraphIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : question.graphs!.length - 1
    );
  };

  const handleNextGraph = () => {
    if (!question.graphs || question.graphs.length <= 1) return;
    setCurrentGraphIndex((prevIndex) =>
      prevIndex < question.graphs!.length - 1 ? prevIndex + 1 : 0
    );
  };

  // Format available information to always be an array
  const formatAvailableInfo = (info: string[] | undefined): string[] => {
    if (!info) return [];
    return info;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-12 bg-gray-200 rounded mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
      </div>
    );
  }

  // Check if we have information to display
  const hasAvailableInfo =
    question.availableInformation &&
    formatAvailableInfo(question.availableInformation).length > 0;

  const hasGraphs = question.graphs && question.graphs.length > 0;

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div>
        {/* Tabs Row */}
        <div className="flex items-center mb-4">
          {questionTabs.map((tab, index) => (
            <React.Fragment key={tab.id}>
              <button
                onClick={() => onTabChange(tab.id)}
                className={`px-4 py-2 text-[14px] border-b-2 ${
                  activeTab === tab.id
                    ? "text-[var(--color-primary)] border-[var(--color-primary)] font-medium"
                    : "text-[#474A52] border-transparent"
                }`}
              >
                Question {tab.number}
              </button>
              {index < questionTabs.length - 1 && (
                <div className="w-[2px] h-6 bg-[#E6E8EB] mx-2"></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Question Content */}
        <div className="py-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-[#1F2024]">
              {question.text}
            </h3>
          </div>
          {/* Additional Information and Graph Buttons */}
          <div className="flex gap-2 mt-2">
            {/* Question Type Button */}
            {question.questionType && (
              <div className="relative">
                <div className="flex items-center gap-1 px-2 py-2 rounded-xl text-xs bg-purple-500 bg-opacity-10 text-[var(--color-primary-light)]">
                  <span>{formatQuestionType(question.questionType)}</span>
                </div>
              </div>
            )}

            {/* Additional Information Button */}
            {hasAvailableInfo && (
              <div className="relative">
                <button
                  onClick={() => {
                    setIsInfoExpanded(!isInfoExpanded);
                    setShowInfoModal(true);
                  }}
                  className="flex items-center gap-1 px-2 py-1 rounded border border-[#E6E8EB] text-[#474A52] text-[14px] hover:bg-[#F5F6F7]"
                >
                  <Info className="w-3 h-3" />
                  <span>Additional Information</span>
                </button>
              </div>
            )}

            {/* Graph Button */}
            {hasGraphs && (
              <div className="relative">
                <button
                  onClick={() => {
                    setIsGraphExpanded(!isGraphExpanded);
                    setShowGraphModal(true);
                  }}
                  className="flex items-center gap-1 px-2 py-1 rounded border border-[#E6E8EB] text-[#474A52] text-[14px] hover:bg-[#F5F6F7]"
                >
                  <BarChart2 className="w-3 h-3" />
                  <span>Graph</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Additional Information Modal */}
        <Modal
          isOpen={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          title="Additional Information"
        >
          <ul className="text-[14px] text-[#474A52] space-y-3">
            {formatAvailableInfo(question.availableInformation).map(
              (info, index) => (
                <li key={index} className="flex items-start">
                  <span className="w-2 h-2 mt-1.5 mr-2 bg-[var(--color-primary)] rounded-full"></span>
                  <span>{info}</span>
                </li>
              )
            )}
          </ul>
        </Modal>

        {/* Graph Modal */}
        <Modal
          isOpen={showGraphModal}
          onClose={() => setShowGraphModal(false)}
          title={
            question.graphs?.[currentGraphIndex]?.graph_title || "Graph View"
          }
        >
          <div className="space-y-6">
            {question.graphs && question.graphs.length > 1 && (
              <div className="flex items-center justify-between">
                <button
                  onClick={handlePreviousGraph}
                  className="text-[#1B58F4] hover:bg-[#F5F6F7] p-2 rounded"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <span className="text-[14px] text-[#474A52]">
                  {currentGraphIndex + 1} of {question.graphs.length}
                </span>
                <button
                  onClick={handleNextGraph}
                  className="text-[#1B58F4] hover:bg-[#F5F6F7] p-2 rounded"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            )}
            <div className="flex justify-center">
              <img
                src={question.graphs?.[currentGraphIndex]?.graph_url}
                alt={question.graphs?.[currentGraphIndex]?.graph_title}
                className="max-w-full rounded-lg shadow-lg"
              />
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default QuestionSection;
